<template>
    <div>
        <div v-if="quotaAILimitReached" class="sellsy-ai">
            <CompassTooltip
                placement="top"
                :delay="{ show: 0, hide: 1 }"
                :disabled="source === 'bubblemenu'"
            >
                <template #content>
                    <span>{{ tooltipLabelTranslate }}</span>
                </template>
                <CompassIconButton
                    v-if="source === 'richtextmenu'"
                    ref="refTriggerPopoverLimitQuota"
                    size="small"
                    @click="displayPopoverLimitQuota = !displayPopoverLimitQuota"
                >
                    <template #default>
                        <SaasIcon icon="aiWrite" width="16" height="16" />
                    </template>
                </CompassIconButton>
                <button
                    v-else-if="source === 'bubblemenu'"
                    ref="refTriggerPopoverLimitQuota"
                    type="button"
                    class="sellsy-ai__button"
                    @click="displayPopoverLimitQuota = !displayPopoverLimitQuota"
                >
                    <span class="sellsy-ai__button-content">
                        <SaasIcon icon="aiWriteSquare" width="16" height="16" />
                        {{ labelIconTranslate }}
                    </span>
                </button>
            </CompassTooltip>

            <CompassPopover
                :placement="getPlacementPopover"
                customClass="sellsy-ai__popover"
                triggerRef="refTriggerPopoverLimitQuota"
                :isVisible="displayPopoverLimitQuota"
                @close="displayPopoverLimitQuota = false"
                @open="handleVisibleChange"
            >
                <div class="sellsy-ai__popover-container">
                    <SaasIcon icon="aiWarning" width="16" height="16" />
                    <p class="label-limit-quota typographies-paragraph-small-regular">
                        {{ labelLimitQuotaTranslate }}
                    </p>
                    <CompassPlainButton
                        :label="labelButtonContactUsTranslate"
                        size="small"
                        @click.prevent="handleContactUs"
                    />
                </div>
            </CompassPopover>
        </div>
        <CompassMenu
            v-else
            :placement="getPlacementMenu"
            size="small"
            customClass="sellsy-ai__menu"
            class="sellsy-ai"
            @open="handleVisibleChange"
        >
            <CompassTooltip
                placement="top"
                :delay="{ show: 0, hide: 1 }"
                :disabled="source === 'bubblemenu'"
            >
                <template #content>
                    <span>{{ tooltipLabelTranslate }}</span>
                </template>
                <CompassIconButton
                    v-if="source === 'richtextmenu'"
                    size="small"
                    :data-bot="`ai-menu-${sourceForLogs}`"
                >
                    <template #default>
                        <SaasIcon icon="aiWrite" width="16" height="16" />
                    </template>
                </CompassIconButton>
                <button
                    v-else-if="source === 'bubblemenu'"
                    type="button"
                    class="sellsy-ai__button"
                    :data-bot="`ai-bubblemenu-${sourceForLogs}`"
                >
                    <span class="sellsy-ai__button-content">
                        <SaasIcon icon="aiWriteSquare" width="16" height="16" />
                        {{ labelIconTranslate }}
                    </span>
                </button>
            </CompassTooltip>
            <template #dropdown>
                <!-- "Améliorer" -->
                <CompassMenuSubtitle :label="menuSubtitleLabelTranslate.improve" />
                <CompassMenuItem
                    :label="menuItemsLabelTranslate.shorten"
                    prefixIcon="ia-sumup"
                    :disabled="isEmptyRichText"
                    :data-bot="`ai-shorten-${sourceForLogs}`"
                    @click="writerServiceAdapter({ task: 'shorten' })"
                />
                <CompassMenuItem
                    :label="menuItemsLabelTranslate.develop"
                    prefixIcon="ia-develop"
                    :disabled="isEmptyRichText"
                    :data-bot="`ai-develop-${sourceForLogs}`"
                    @click="writerServiceAdapter({ task: 'develop' })"
                />
                <CompassMenuGroup
                    :label="menuItemsLabelTranslate.changeTone.default"
                    placement="right-start"
                    icon="ia-change-tone"
                    :disabled="isEmptyRichText"
                    customClass="sellsy-ai__menu-group"
                >
                    <template #dropdown>
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.changeTone.professional"
                            :data-bot="`ai-tone-professional-${sourceForLogs}`"
                            @click="writerServiceAdapter({ tone: 'professional' })"
                        />
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.changeTone.informal"
                            :data-bot="`ai-tone-informal-${sourceForLogs}`"
                            @click="writerServiceAdapter({ tone: 'informal' })"
                        />
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.changeTone.friendly"
                            :data-bot="`ai-tone-friendly-${sourceForLogs}`"
                            @click="writerServiceAdapter({ tone: 'friendly' })"
                        />
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.changeTone.persuasive"
                            :data-bot="`ai-tone-persuasive-${sourceForLogs}`"
                            @click="writerServiceAdapter({ tone: 'persuasive' })"
                        />
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.changeTone.direct"
                            :data-bot="`ai-tone-direct-${sourceForLogs}`"
                            @click="writerServiceAdapter({ tone: 'direct' })"
                        />
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.changeTone.informative"
                            :data-bot="`ai-tone-informative-${sourceForLogs}`"
                            @click="writerServiceAdapter({ tone: 'informative' })"
                        />
                    </template>
                </CompassMenuGroup>
                <CompassMenuItem
                    :label="menuItemsLabelTranslate.correct"
                    icon="ia-correct"
                    :disabled="isEmptyRichText"
                    :data-bot="`ai-correct-${sourceForLogs}`"
                    @click="writerServiceAdapter({ task: 'correct' })"
                />
                <CompassMenuItem
                    :label="menuItemsLabelTranslate.improve"
                    prefixIcon="ia-form"
                    :disabled="isEmptyRichText"
                    :data-bot="`ai-improve-${sourceForLogs}`"
                    @click="writerServiceAdapter({ task: 'improve' })"
                />
                <CompassMenuGroup
                    :label="menuItemsLabelTranslate.translate.default"
                    placement="right-start"
                    prefixIcon="ia-translate"
                    :disabled="isEmptyRichText"
                    customClass="sellsy-ai__menu-group"
                >
                    <template #dropdown>
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.translate.english"
                            :data-bot="`ai-translate-english-${sourceForLogs}`"
                            @click="writerServiceAdapter({ language: 'english' })"
                        />
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.translate.german"
                            :data-bot="`ai-translate-german-${sourceForLogs}`"
                            @click="writerServiceAdapter({ language: 'german' })"
                        />
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.translate.spanish"
                            :data-bot="`ai-translate-spanish-${sourceForLogs}`"
                            @click="writerServiceAdapter({ language: 'spanish' })"
                        />
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.translate.italian"
                            :data-bot="`ai-translate-italian-${sourceForLogs}`"
                            @click="writerServiceAdapter({ language: 'italian' })"
                        />
                        <CompassMenuItem
                            :label="menuItemsLabelTranslate.translate.french"
                            :data-bot="`ai-translate-french-${sourceForLogs}`"
                            @click="writerServiceAdapter({ language: 'french' })"
                        />
                    </template>
                </CompassMenuGroup>
                <!-- "Rédiger" -->
                <template v-if="showWritingMenu">
                    <CompassMenuSubtitle :label="menuSubtitleLabelTranslate.writing" />
                    <CompassMenuItem
                        :label="menuItemsLabelTranslate.prospect"
                        prefixIcon="ia-prospecting"
                        :disabled="disabled"
                        :data-bot="`ai-prospect-${sourceForLogs}`"
                        @click="writerServiceAdapter({ task: 'prospect', write: true })"
                    />
                    <CompassMenuItem
                        :label="menuItemsLabelTranslate.followUp"
                        prefixIcon="ia-tracking"
                        :disabled="disabled"
                        :data-bot="`ai-followup-${sourceForLogs}`"
                        @click="writerServiceAdapter({ task: 'followUp', write: true })"
                    />
                    <CompassMenuItem
                        :label="menuItemsLabelTranslate.reminder"
                        prefixIcon="ia-reminder"
                        :disabled="disabled"
                        :data-bot="`ai-reminder-${sourceForLogs}`"
                        @click="writerServiceAdapter({ task: 'reminder', write: true })"
                    />
                    <CompassMenuItem
                        :label="menuItemsLabelTranslate.promotion"
                        prefixIcon="ia-promote"
                        :disabled="disabled"
                        :data-bot="`ai-promotion-${sourceForLogs}`"
                        @click="writerServiceAdapter({ task: 'promotion', write: true })"
                    />
                    <CompassMenuItem
                        :label="menuItemsLabelTranslate.thanks"
                        prefixIcon="ia-thank-you"
                        :disabled="disabled"
                        :data-bot="`ai-thanks-${sourceForLogs}`"
                        @click="writerServiceAdapter({ task: 'thanks', write: true })"
                    />
                </template>
                <template v-if="source === 'richtextmenu' && showAIQuotaUpsell">
                    <hr
                        v-if="remainingQuotaAI && !loadingRemainingQuotaAI"
                        class="sellsy-ai__menu__divider"
                    />
                    <div
                        v-if="remainingQuotaAI && !loadingRemainingQuotaAI"
                        class="sellsy-ai__menu__remaining-quota, sellsy-ai__menu__remaining-quota--hover"
                        @click="handleUpsell"
                    >
                        <span
                            class="sellsy-ai__menu__remaining-quota-label typographies-paragraph-x-small-semibold"
                        >
                            {{ labelRemainingQuotaTranslate }}
                        </span>
                        <div class="sellsy-ai__menu__remaining-quota-upsell">
                            <span class="typographies-paragraph-x-small-regular">
                                {{ upsellLabel }}
                            </span>
                            <CompassFeatureBadge
                                isInteractive
                                size="extra-small"
                                variant="upsell"
                            />
                        </div>
                    </div>
                    <CompassSkeleton v-else size="large" />
                </template>
            </template>
        </CompassMenu>
    </div>
</template>

<script>
import SellsyAIRichTextMixin from 'mixins/SellsyAIRichTextMixin';
import Feature from 'Services/Feature';
import Store from 'stores/ai';
import { createNamespacedHelpers } from 'vuex';
import SaasIcon from 'vcomponents/common/SaasIcon.vue';
const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
const corpMapState = createNamespacedHelpers('corp').mapState;

export default {
    name: 'SellsyAIMenu',
    components: { SaasIcon },
    mixins: [SellsyAIRichTextMixin],
    inject: ['parentHash'],
    props: {
        editor: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        typeRichText: {
            type: String,
            required: true,
        },
        source: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            menuSubtitleLabelTranslate: {
                improve: this.$t.__('ai.SellsyAIMenu.menu_subtitle_improve'),
                writing: this.$t.__('ai.SellsyAIMenu.menu_subtitle_writing'),
            },
            menuItemsLabelTranslate: {
                shorten: this.$t.__('ai.SellsyAIMenu.menu_item_shorten'),
                develop: this.$t.__('ai.SellsyAIMenu.menu_item_develop'),
                changeTone: {
                    default: this.$t.__('ai.SellsyAIMenu.menu_item_changetone_default'),
                    professional: this.$t.__('ai.SellsyAIMenu.menu_item_changetone_professional'),
                    informal: this.$t.__('ai.SellsyAIMenu.menu_item_changetone_informal'),
                    friendly: this.$t.__('ai.SellsyAIMenu.menu_item_changetone_friendly'),
                    persuasive: this.$t.__('ai.SellsyAIMenu.menu_item_changetone_persuasive'),
                    direct: this.$t.__('ai.SellsyAIMenu.menu_item_changetone_direct'),
                    informative: this.$t.__('ai.SellsyAIMenu.menu_item_changetone_informative'),
                },
                correct: this.$t.__('ai.SellsyAIMenu.menu_item_correct'),
                improve: this.$t.__('ai.SellsyAIMenu.menu_item_improve'),
                translate: {
                    default: this.$t.__('ai.SellsyAIMenu.menu_item_translate_default'),
                    english: this.$t.__('ai.SellsyAIMenu.menu_item_translate_english'),
                    german: this.$t.__('ai.SellsyAIMenu.menu_item_translate_german'),
                    spanish: this.$t.__('ai.SellsyAIMenu.menu_item_translate_spanish'),
                    italian: this.$t.__('ai.SellsyAIMenu.menu_item_translate_italian'),
                    french: this.$t.__('ai.SellsyAIMenu.menu_item_translate_french'),
                },
                prospect: this.$t.__('ai.SellsyAIMenu.menu_item_prospect'),
                followUp: this.$t.__('ai.SellsyAIMenu.menu_item_followup'),
                reminder: this.$t.__('ai.SellsyAIMenu.menu_item_reminder'),
                promotion: this.$t.__('ai.SellsyAIMenu.menu_item_promotion'),
                thanks: this.$t.__('ai.SellsyAIMenu.menu_item_thanks'),
            },
            labelIconTranslate: this.$t.__('ai.SellsyAIMenu.label_icon'),
            labelLimitQuotaTranslate: this.$t.__('ai.SellsyAIMenu.label_limit_quota'),
            labelButtonContactUsTranslate: this.$t.__('ai.SellsyAIMenu.label_button_contact_us'),
            hash: null,
            remainingQuotaAI: null,
            loadingRemainingQuotaAI: false,
            displayPopoverLimitQuota: false,
            upsellLabel: this.$t.__('ai.SellsyAIMenu.upsell_label'),
            sourceForLogs: this.typeRichText,
        };
    },
    computed: {
        ...corpMapState(['plan', 'pricingVersion']),
        tooltipLabelTranslate() {
            return this.$t.__('richtext.sellsy_ai');
        },
        isEmptyRichText() {
            return this.editor.getHTML() === '<p></p>';
        },
        showWritingMenu() {
            return this.typeRichText === 'email' && this.source !== 'bubblemenu';
        },
        getPlacementMenu() {
            let placement = 'bottom-start';
            if (this.source === 'bubblemenu') {
                placement = 'right-end';
            } else if (this.typeRichText === 'email') {
                placement = 'top-start';
            }
            return placement;
        },
        getPlacementPopover() {
            return this.source === 'bubblemenu' ? 'bottom-start' : 'top-start';
        },
        labelRemainingQuotaTranslate() {
            return this.$t.n__(
                'ai.SellsyAIMenu.label_remaining_quota',
                null,
                this.remainingQuotaAI,
                this.remainingQuotaAI,
            );
        },
        showAIQuotaUpsell() {
            // Hide upsell and remaining quota ai for ELITE and EVOLUTION plans with pricing version 5
            if (this.plan && this.pricingVersion === 5) {
                return !(this.plan.includes('ELITE') || this.plan.includes('EVOLUTION'));
            }
            return true;
        },
    },
    created() {
        this.hash =
            this.source === 'bubblemenu' ? `ai-${this.getParentHash()}` : `ai-${this.$parent.hash}`;
        if (!this.$store.state[this.hash]) {
            this.$store.registerModule(this.hash, Store);
        }
        this.$store.dispatch(`${this.hash}/setTypeRichText`, this.typeRichText);
        if (this.$route.path.includes('templates')) {
            this.sourceForLogs = 'email-template';
            this.$store.dispatch(`${this.hash}/setSource`, this.sourceForLogs);
        }
    },
    unmounted() {
        if (this.$store.state[this.hash]) {
            this.$store.unregisterModule(this.hash, Store);
        }
    },
    methods: {
        ...popupPanelMapActions(['open']),
        getParentHash() {
            let iterator = 0;
            let parent = this.$parent;
            while (parent && !parent.hash && iterator < 5) {
                parent = parent?.$parent;
                iterator++;
            }
            return parent.hash;
        },
        async getRemainingQuotaAI() {
            await this.$store.dispatch('corp/fetchQuotaAI');
            const { limit, used } = this.getQuotaAI;
            this.remainingQuotaAI = limit - used;
            this.loadingRemainingQuotaAI = false;
        },
        handleContactUs() {
            if (Feature.isEnabled('intercom')) {
                window.Intercom('show');
            }
        },
        async handleVisibleChange() {
            this.loadingRemainingQuotaAI = true;
            await this.getRemainingQuotaAI();
            this.$store.dispatch(`${this.hash}/setUseAI`, true);
        },
        handleUpsell() {
            this.open({
                key: 'featureModal',
                props: {
                    feature: 'upsell-ai',
                    type: 'upsell-ai',
                },
            });
        },
    },
};
</script>

<style lang="scss">
.sellsy-ai {
    &__menu,
    &__popover {
        z-index: 1000 !important;
    }
    &__menu {
        &-group {
            z-index: 1001 !important;
        }

        .compass-icon:not(:nth-child(2)):not(:last-child) {
            color: var(--colors-common-icon-ai-default);
        }

        .compass-menu__dropdown__content {
            max-height: none !important;
        }

        &__remaining-quota {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            padding: var(--spacing-0);
            cursor: default;
            user-select: none;

            &--hover:hover {
                border-radius: var(--spacing-0);
                background: var(--colors-common-background-highlight-ai);
            }

            &-label {
                color: var(--colors-common-text-standard-caption);
            }
            &-upsell {
                display: flex;
                gap: var(--spacing-0);
            }
        }
        &__divider {
            height: 1px;
            margin: 0 var(--spacing-1);
            border: none;
            background: var(--colors-common-border-lighter);
        }
    }
    &__popover {
        .compass-popover__content {
            padding: var(--spacing-1);
        }
        &-container {
            display: flex;
            align-items: center;
            gap: var(--spacing-0);

            .label-limit-quota {
                margin: 0;
            }
        }
    }
    &__button {
        position: relative;
        cursor: pointer;
        border-radius: var(--spacing-0);
        border: 1px solid transparent;
        padding: 0;
        background: var(--colors-common-background-default);
        box-shadow: var(--box-shadows-small);
        transition: all 100ms cubic-bezier(0, 0, 0.3, 1);
        &:hover {
            background:
                linear-gradient(#fff, #fff) padding-box,
                var(--colors-sellsy-ai-gradient) border-box;
        }

        &-content {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: var(--spacing-0);
            gap: var(--spacing-0);
            font-family: var(--font-families-lato);
            font-size: 12px;
            font-weight: 600;
            line-height: 16px;
            letter-spacing: 0px;
            color: var(--colors-common-text-standard-default);
        }
    }
}
</style>
